/**
 * React hooks for integration components
 * Placeholder - Hooks will be added as components are implemented
 */

import { useState, useCallback } from 'react';

/**
 * Hook for managing asset selection
 * @returns Asset selection state and handlers
 */
export function useAssetSelection<T extends { id: string }>() {
  const [selectedAssets, setSelectedAssets] = useState<T[]>([]);

  const toggleSelection = useCallback((asset: T) => {
    setSelectedAssets((prev) => {
      const isSelected = prev.some((a) => a.id === asset.id);
      if (isSelected) {
        return prev.filter((a) => a.id !== asset.id);
      }
      return [...prev, asset];
    });
  }, []);

  const selectAll = useCallback((assets: T[]) => {
    setSelectedAssets(assets);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedAssets([]);
  }, []);

  const isSelected = useCallback(
    (assetId: string) => selectedAssets.some((a) => a.id === assetId),
    [selectedAssets]
  );

  return {
    selectedAssets,
    toggleSelection,
    selectAll,
    clearSelection,
    isSelected,
    selectionCount: selectedAssets.length,
  };
}

/**
 * Hook for managing folder navigation
 * @returns Folder navigation state and handlers
 */
export function useFolderNavigation(rootFolderId = 'root') {
  const [currentFolderId, setCurrentFolderId] = useState<string>(rootFolderId);
  const [folderHistory, setFolderHistory] = useState<string[]>([]);

  const navigateTo = useCallback((folderId: string) => {
    setFolderHistory((prev) => [...prev, currentFolderId]);
    setCurrentFolderId(folderId);
  }, [currentFolderId]);

  const goBack = useCallback(() => {
    if (folderHistory.length === 0) return;
    
    const previousFolderId = folderHistory[folderHistory.length - 1];
    setFolderHistory((prev) => prev.slice(0, -1));
    if (previousFolderId !== undefined) {
      setCurrentFolderId(previousFolderId);
    }
  }, [folderHistory]);

  const goToRoot = useCallback(() => {
    setFolderHistory([]);
    setCurrentFolderId(rootFolderId);
  }, [rootFolderId]);

  const canGoBack = folderHistory.length > 0;
  const isAtRoot = currentFolderId === rootFolderId;

  return {
    currentFolderId,
    folderHistory,
    navigateTo,
    goBack,
    goToRoot,
    canGoBack,
    isAtRoot,
  };
}

/**
 * Hook for managing search state
 * @returns Search state and handlers
 */
export function useSearch(debounceMs = 300) {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  const updateQuery = useCallback((newQuery: string) => {
    setQuery(newQuery);
    setIsSearching(true);
    
    // Simple debounce implementation
    const timeoutId = setTimeout(() => {
      setDebouncedQuery(newQuery);
      setIsSearching(false);
    }, debounceMs);

    return () => clearTimeout(timeoutId);
  }, [debounceMs]);

  const clearSearch = useCallback(() => {
    setQuery('');
    setDebouncedQuery('');
    setIsSearching(false);
  }, []);

  return {
    query,
    debouncedQuery,
    isSearching,
    updateQuery,
    clearSearch,
  };
}